"use client";

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from "react";
import {
    authService,
    AuthTokens,
    User,
    LoginCredentials,
    RegisterData,
} from "../api/authService";
import {
    reportAnalyticsEvent,
    pushUserIdToDataLayer,
} from "@/features/analytics/application/reportAnalyticsEvent";
import { AnalyticsEventType } from "@/features/analytics/domain/entities/AnalyticsEventType";
import { reportInfo } from "@/common/application/debug";

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (
        credentials: LoginCredentials
    ) => Promise<{ success: boolean; error?: string }>;
    register: (
        data: RegisterData,
        redirectUrl?: string
    ) => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
    verifyEmailAndLogin: (
        oobCode: string
    ) => Promise<{ success: boolean; error?: string }>;
    refreshAuth: () => Promise<void>;
    fetchUser: () => Promise<User | null>;
    sendPasswordReset: (
        email: string,
        returnUrl?: string
    ) => Promise<{ success: boolean; error?: string }>;
    getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = "idToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const TOKEN_EXPIRY_KEY = "tokenExpiry";

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Helper function to store tokens
    const storeTokens = useCallback((tokens: AuthTokens) => {
        if (typeof window === "undefined") return;
        localStorage.setItem(TOKEN_KEY, tokens.idToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        localStorage.setItem(
            TOKEN_EXPIRY_KEY,
            String(Date.now() + tokens.expiresIn * 1000)
        );
        // Notify other tabs about login via BroadcastChannel
        // (storage events will also fire automatically)
        if (typeof window !== "undefined" && "BroadcastChannel" in window) {
            const channel = new BroadcastChannel("auth_channel");
            channel.postMessage({ type: "LOGIN" });
            channel.close();
        }
    }, []);

    // Helper function to clear tokens
    const clearTokens = useCallback(() => {
        if (typeof window === "undefined") return;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        // Notify other tabs about logout
        if (typeof window !== "undefined" && "BroadcastChannel" in window) {
            const channel = new BroadcastChannel("auth_channel");
            channel.postMessage({ type: "LOGOUT" });
            channel.close();
        }
    }, []);

    // Helper function to get current token
    const getCurrentToken = useCallback((): string | null => {
        if (typeof window === "undefined") return null;
        const token = localStorage.getItem(TOKEN_KEY);
        const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

        if (!token || !expiry) return null;

        // Check if token expired
        if (Date.now() > parseInt(expiry)) {
            clearTokens();
            return null;
        }

        return token;
    }, [clearTokens]);

    // Fetch user data with current token
    const fetchUser = useCallback(async () => {
        const token = getCurrentToken();

        if (!token) {
            setUser(null);
            setIsAuthenticated(false);
            return null;
        }

        try {
            const userData = await authService.getCurrentUser(token);
            if (userData) {
                setUser(userData);
                setIsAuthenticated(true);
                reportInfo(`Fetched user data for user ID: ${userData.id}`);
                // Push user_id and email directly to GTM dataLayer for automatic events (like page views)
                // and Google Ads Enhanced Conversions
                void pushUserIdToDataLayer(userData.id, userData.email);
                return userData;
            } else {
                clearTokens();
                setUser(null);
                setIsAuthenticated(false);
                void pushUserIdToDataLayer(null, null);
                return null;
            }
        } catch (error) {
            clearTokens();
            setUser(null);
            setIsAuthenticated(false);
            void pushUserIdToDataLayer(null, null);
            return null;
        }
    }, [getCurrentToken, clearTokens]);

    // Login function
    const login = useCallback(
        async (
            credentials: LoginCredentials
        ): Promise<{ success: boolean; error?: string }> => {
            try {
                const tokens = await authService.login(credentials);

                if (tokens) {
                    storeTokens(tokens);
                    const userData = await fetchUser();
                    if (userData) {
                        // Track successful login after user is set
                        void reportAnalyticsEvent({
                            eventType: AnalyticsEventType.login,
                            label: "User Login",
                            metadata: {
                                method: "basic",
                            },
                        });
                        return { success: true };
                    }
                    return {
                        success: false,
                        error: "Failed to fetch user data",
                    };
                }

                return { success: false, error: "Invalid credentials" };
            } catch (error: any) {
                return {
                    success: false,
                    error: error.response?.data?.message || "Login failed",
                };
            }
        },
        [storeTokens, fetchUser]
    );

    // Register function
    const register = useCallback(
        async (
            data: RegisterData,
            returnUrl?: string
        ): Promise<{ success: boolean; error?: string }> => {
            try {
                // Build the redirect URL with the return path
                const baseUrl =
                    typeof window !== "undefined"
                        ? window.location.origin
                        : "http://localhost:3000";
                let redirectUrl = `${baseUrl}/verify-email`;

                if (returnUrl) {
                    // Encode the return URL as a query parameter
                    redirectUrl += `?returnUrl=${encodeURIComponent(returnUrl)}`;
                }

                const result = await authService.register(data, redirectUrl);
                return result;
            } catch (error: any) {
                return {
                    success: false,
                    error:
                        error.response?.data?.message || "Registration failed",
                };
            }
        },
        []
    );

    // Verify email and login function
    const verifyEmailAndLogin = useCallback(
        async (
            oobCode: string
        ): Promise<{ success: boolean; error?: string }> => {
            try {
                const tokens = await authService.verifyAndLogin(oobCode);

                if (tokens) {
                    storeTokens(tokens);
                    const userData = await fetchUser();
                    if (userData) {
                        // Track successful registration (email confirmed) after user is set
                        void reportAnalyticsEvent({
                            eventType: AnalyticsEventType.signUp,
                            label: "User Registration Complete",
                            metadata: {
                                method: "email_verification",
                            },
                        });
                        return { success: true };
                    }
                    return {
                        success: false,
                        error: "Failed to fetch user data",
                    };
                }

                return { success: false, error: "Verification failed" };
            } catch (error: any) {
                return {
                    success: false,
                    error:
                        error.response?.data?.message ||
                        "Email verification failed",
                };
            }
        },
        [storeTokens, fetchUser]
    );

    // Password reset function
    const sendPasswordReset = useCallback(
        async (
            email: string,
            returnUrl?: string
        ): Promise<{ success: boolean; error?: string }> => {
            try {
                // Build the redirect URL for password reset (similar to registration)
                const baseUrl =
                    typeof window !== "undefined"
                        ? window.location.origin
                        : "http://localhost:3000";
                let redirectUrl = `${baseUrl}/reset-password`;

                if (returnUrl) {
                    // Encode the return URL as a query parameter
                    redirectUrl += `?returnUrl=${encodeURIComponent(returnUrl)}`;
                }

                const success = await authService.sendPasswordReset(
                    email,
                    redirectUrl
                );
                if (success) {
                    return { success: true };
                }
                return { success: false, error: "Failed to send reset email" };
            } catch (error: any) {
                return {
                    success: false,
                    error:
                        error.response?.data?.message ||
                        "Failed to send reset email",
                };
            }
        },
        []
    );

    // Logout function
    const logout = useCallback(() => {
        // Clear user_id and email from dataLayer
        void pushUserIdToDataLayer(null, null);

        // Fire logout event
        void reportAnalyticsEvent({
            eventType: AnalyticsEventType.logout,
            label: "User Logout",
            metadata: {
                action: "logout",
            },
        });

        clearTokens();
        setUser(null);
        setIsAuthenticated(false);
    }, [clearTokens]);

    // Refresh auth (check token and refresh if needed)
    const refreshAuth = useCallback(async () => {
        if (typeof window === "undefined") return;
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

        if (!refreshToken) return;

        // Check if token is about to expire (within 5 minutes)
        if (expiry && Date.now() > parseInt(expiry) - 5 * 60 * 1000) {
            try {
                const tokens = await authService.refresh(refreshToken);
                if (tokens) {
                    storeTokens(tokens);
                    await fetchUser();
                }
            } catch (error) {
                logout();
            }
        }
    }, [storeTokens, fetchUser, logout]);

    // Initialize auth state on mount
    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await fetchUser();
            setIsLoading(false);
        };

        void init();
    }, [fetchUser]);

    // Set up token refresh interval
    useEffect(() => {
        const interval = setInterval(() => {
            void refreshAuth();
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, [refreshAuth]);

    // Sync auth state across tabs
    useEffect(() => {
        // Storage event listener (native browser event for cross-tab sync)
        const handleStorageChange = (e: StorageEvent) => {
            // Only respond to TOKEN_KEY changes to avoid duplicate fetchUser calls
            if (e.key === TOKEN_KEY) {
                if (e.newValue) {
                    // Token added/changed - login in another tab
                    // Add small delay to ensure localStorage has propagated
                    setTimeout(() => {
                        void fetchUser();
                    }, 100);
                } else {
                    // Token removed - logout in another tab
                    setUser(null);
                    setIsAuthenticated(false);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);

        // BroadcastChannel listener (backup for browsers that support it)
        let channel: BroadcastChannel | null = null;
        if (typeof window !== "undefined" && "BroadcastChannel" in window) {
            channel = new BroadcastChannel("auth_channel");
            channel.onmessage = (event) => {
                if (event.data.type === "LOGIN") {
                    // Add small delay to ensure localStorage has propagated
                    setTimeout(() => {
                        void fetchUser();
                    }, 100);
                } else if (event.data.type === "LOGOUT") {
                    setUser(null);
                    setIsAuthenticated(false);
                }
            };
        }

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            channel?.close();
        };
    }, [fetchUser]);

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated,
                isLoading,
                login,
                register,
                logout,
                verifyEmailAndLogin,
                refreshAuth,
                fetchUser,
                sendPasswordReset,
                getToken: getCurrentToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
